import { useRef } from 'react';
import { FormField } from 'ui/formField';
import { PushContentDefinition } from 'modules/graphql/types';
import { LabelWithLink } from 'modules/templateEditor/common';
import { TextInputField } from 'modules/templateEditor/common/components';
import { RestrictionBadge } from '../RestrictionBadge';
import { getIsEditMode, constructFieldErrors } from '../../common/utils';
import { SETTINGS_DISPLAY_CONTENT } from '../constants';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { constructErrorInlineText } from '../../common/ErrorInlineText';
import { FieldNames } from '../../common/types';
import { useErrorValidationContext } from '../../context/ErrorValidationContext';

/*
  A component used to select the interruption level
  for Push templates.
*/
export const BadgeCount = () => {
  const { view } = useQueryParamContext();
  const { isEditMode, liveTemplate, setCurrentMediumField } = useEditableFieldsContext();
  const { localeContentErrors, localeContentWarnings } = useErrorValidationContext();
  const pushData = (liveTemplate?.mediumData || {}) as PushContentDefinition;
  const isEditModeVisible = getIsEditMode(view, isEditMode);
  const badgeRef = useRef<HTMLInputElement | null>(null);

  const { hasError: hasBadgeError, hasWarning: hasBadgeWarning } = constructFieldErrors({
    contentErrors: localeContentErrors,
    contentWarnings: localeContentWarnings,
    fieldName: FieldNames.BADGE,
  });

  const badgeErrorText = constructErrorInlineText({
    condition: isEditModeVisible,
    hasError: hasBadgeError,
    hasWarning: hasBadgeWarning,
  });

  return (
    <FormField
      hasError={hasBadgeError}
      hasWarning={hasBadgeWarning}
      errorText={badgeErrorText}
      helpText={
        isEditModeVisible ? (
          <LabelWithLink
            text={SETTINGS_DISPLAY_CONTENT.badgeCount.helpText}
            url={SETTINGS_DISPLAY_CONTENT.badgeCount.helpLink.link}
            openInNewTab
          />
        ) : (
          ''
        )
      }
      isHorizontalLayout
      labelText={SETTINGS_DISPLAY_CONTENT.badgeCount.labelText}
      restrictionText={<RestrictionBadge text={SETTINGS_DISPLAY_CONTENT.badgeCount.restriction} />}
    >
      <TextInputField
        hasError={hasBadgeError}
        mediumRef={badgeRef}
        mediumDataKey={'badge'}
        mediumDataValue={pushData.badge || ''}
        changeHandler={(badgeVal) => setCurrentMediumField('badge', badgeVal)}
        placeholderText={SETTINGS_DISPLAY_CONTENT.badgeCount.labelText}
        showSnippet
      />
    </FormField>
  );
};
